// ============================================
// BillingDashboard Component
// ============================================
// Main billing overview showing subscription status,
// usage, and links to manage billing
// ============================================

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './BillingDashboard.css';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import AppFooter from '../AppFooter';
import { FaHome, FaReceipt, FaCreditCard } from 'react-icons/fa';

const BillingDashboard = ({ tenantId }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();
  const actualTenantId = tenantId || params.tenant;
  const [subscription, setSubscription] = useState(null);
  const [usage, setUsage] = useState(null);
  const [cost, setCost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchBillingData();
  }, [actualTenantId]);

  const fetchBillingData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch subscription, usage, and cost in parallel (allow partial failures)
      const [subRes, usageRes, costRes] = await Promise.allSettled([
        axios.get(`/api/billing/subscription/${actualTenantId}`),
        axios.get(`/api/billing/usage/${actualTenantId}`),
        axios.get(`/api/billing/usage/${actualTenantId}/cost`)
      ]);

      if (subRes.status === 'fulfilled' && subRes.value.data.success) {
        setSubscription(subRes.value.data.subscription);
      }
      // No subscription is valid - user hasn't subscribed yet

      if (usageRes.status === 'fulfilled' && usageRes.value.data.success) {
        setUsage(usageRes.value.data.usage);
      }

      if (costRes.status === 'fulfilled' && costRes.value.data.success) {
        setCost(costRes.value.data.cost);
      }
    } catch (err) {
      console.error('Error fetching billing data:', err);
      setError(t('billing.loadErrorMessage'));
    } finally {
      setLoading(false);
    }
  };

  const handleOpenPortal = async () => {
    try {
      const res = await axios.get(`/api/billing/portal/${actualTenantId}`);
      if (res.data.success && res.data.url) {
        // Stripe Customer Portal URLs live under billing.stripe.com.
        // safeRedirect blocks anything else so a tampered response can't
        // phish-redirect the user under our domain.
        const { safeRedirect } = await import('../../utils/safeRedirect');
        if (!safeRedirect(res.data.url, ['stripe.com'])) {
          alert(t('billing.portalErrorMessage'));
        }
      }
    } catch (err) {
      console.error('Error opening portal:', err);
      alert(t('billing.portalErrorMessage'));
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm(t('billing.cancelConfirmationMessage'))) {
      return;
    }

    try {
      await axios.post(`/api/billing/subscription/${actualTenantId}/cancel`);
      alert(t('billing.cancelScheduledMessage'));
      fetchBillingData();
    } catch (err) {
      console.error('Error canceling subscription:', err);
      alert(t('billing.cancelErrorMessage'));
    }
  };

  const handleReactivateSubscription = async () => {
    try {
      await axios.post(`/api/billing/subscription/${actualTenantId}/reactivate`);
      alert(t('billing.reactivateSuccessMessage'));
      fetchBillingData();
    } catch (err) {
      console.error('Error reactivating subscription:', err);
      alert(t('billing.reactivateErrorMessage'));
    }
  };

  if (loading) {
    return (
      <div className="billing-dashboard">
        <div className="loading">{t('billing.loadingText')}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="billing-dashboard">
        <div className="error">{error}</div>
      </div>
    );
  }

  if (!subscription) {
    return (
      <div className="billing-dashboard" style={{ position: 'relative', paddingBottom: '72px' }}>
        <h1>{t('billing.billingSubscriptionTitle')}</h1>

        {/* Desktop inline action bar (replaces footer on desktop) */}
        <div className="desktop-action-bar">
          <button type="button" onClick={() => navigate(`/${actualTenantId}/dashboard`)}>
            <FaHome /> Home
          </button>
          <button type="button" onClick={() => navigate(`/${actualTenantId}/billing/subscription`)}>
            <FaReceipt /> Subscription
          </button>
          <button type="button" className="btn-primary-action" onClick={() => navigate(`/${actualTenantId}/billing/payment-methods`)}>
            <FaCreditCard /> Payment
          </button>
        </div>

        <div className="no-subscription">
          <h2>{t('billing.noSubscriptionTitle') || 'No Active Subscription'}</h2>
          <p>{t('billing.setupBillingMessage') || 'Choose a plan to get started with On Screen.'}</p>
          <div className="quick-access-buttons" style={{ marginTop: '16px' }}>
            <button
              className="btn-primary"
              onClick={() => navigate(`/${actualTenantId}/billing/subscription`)}
              style={{ marginRight: '10px' }}
            >
              <FaReceipt style={{ marginRight: '8px' }} />
              {t('billing.setupBillingButton') || 'Choose a Plan'}
            </button>
            <button
              className="btn-secondary"
              onClick={() => navigate(`/${actualTenantId}/billing/payment-methods`)}
            >
              <FaCreditCard style={{ marginRight: '8px' }} />
              Payment Methods
            </button>
          </div>
        </div>

        <AppFooter
          buttons={[
            {
              icon: <FaReceipt />,
              label: "Subscription",
              onClick: () => navigate(`/${actualTenantId}/billing/subscription`),
            },
            {
              icon: <FaHome />,
              label: "Home",
              onClick: () => navigate(`/${actualTenantId}/dashboard`),
            },
            {
              icon: <FaCreditCard />,
              label: "Payment",
              onClick: () => navigate(`/${actualTenantId}/billing/payment-methods`),
            },
          ]}
        />
      </div>
    );
  }

  // Detect expired trial (status is TRIALING but trial_end has passed)
  const isExpiredTrial = subscription.status === 'TRIALING'
    && subscription.trial_end
    && new Date(subscription.trial_end) < new Date()
    && subscription.trial_days_remaining <= 0;

  const getStatusColor = (status) => {
    if (isExpiredTrial) return '#dc3545';
    switch (status) {
      case 'ACTIVE': return '#28a745';
      case 'TRIALING': return '#ff6800';
      case 'PAST_DUE': return '#dc3545';
      case 'CANCELED': return '#6c757d';
      default: return '#6c757d';
    }
  };

  const getStatusDisplay = () => {
    if (isExpiredTrial) return 'Trial Expired';
    return subscription.status_display;
  };

  const formatDate = (dateString) => {
    if (!dateString) {
      // Fallback: show current month boundaries for display purposes
      return null;
    }
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getUsagePercentage = (current, limit, isUnlimited) => {
    if (isUnlimited) return 0;
    return Math.min(100, (current / limit) * 100);
  };

  return (
    <div className="billing-dashboard" style={{ position: 'relative', paddingBottom: '72px' }}>
      <h1>{t('billing.billingSubscriptionTitle')}</h1>

      {/* Desktop inline action bar (replaces footer on desktop) */}
      <div className="desktop-action-bar">
        <button type="button" onClick={() => navigate(`/${actualTenantId}/dashboard`)}>
          <FaHome /> Home
        </button>
        <button type="button" onClick={() => navigate(`/${actualTenantId}/billing/subscription`)}>
          <FaReceipt /> Subscription
        </button>
        <button type="button" className="btn-primary-action" onClick={() => navigate(`/${actualTenantId}/billing/payment-methods`)}>
          <FaCreditCard /> Payment
        </button>
      </div>

      {/* Quick Access Navigation */}
      <div className="billing-card">
        <div className="card-header">
          <h2>Quick Access</h2>
        </div>
        <div className="card-body">
          <div className="quick-access-buttons">
            <button
              className="btn-primary"
              onClick={() => navigate(`/${actualTenantId}/billing/subscription`)}
              style={{ marginRight: '10px' }}
            >
              <FaReceipt style={{ marginRight: '8px' }} />
              Manage Subscription
            </button>
            <button
              className="btn-secondary"
              onClick={() => navigate(`/${actualTenantId}/billing/payment-methods`)}
            >
              <FaCreditCard style={{ marginRight: '8px' }} />
              Payment Methods
            </button>
          </div>
        </div>
      </div>

      {/* Subscription Status Card */}
      <div className="billing-card">
        <div className="card-header">
          <h2>{t('billing.currentPlanHeader')}</h2>
          <span
            className="status-badge"
            style={{ backgroundColor: getStatusColor(subscription.status) }}
          >
            {getStatusDisplay()}
          </span>
        </div>
        <div className="card-body">
          <div className="plan-info">
            <div className="plan-name">{subscription.plan_name}</div>
            <div className="plan-price">
              ${subscription.price_monthly}{t('billing.pricePerMonth')}
            </div>
          </div>

          {subscription.status === 'TRIALING' && subscription.trial_days_remaining > 0 && (
            <div className="trial-info">
              <strong>{subscription.trial_days_remaining} {t('billing.trialDaysRemainingSuffix')}</strong>
              <div className="trial-end">{t('billing.trialEndsOnPrefix')}{formatDate(subscription.trial_end)}</div>
            </div>
          )}

          {subscription.cancel_at_period_end && (
            <div className="cancellation-notice">
              <strong>{t('billing.subscriptionWillCancelPrefix')}</strong>{formatDate(subscription.current_period_end)}
              <button
                className="btn-secondary"
                onClick={handleReactivateSubscription}
              >
                {t('billing.reactivateSubscriptionButton')}
              </button>
            </div>
          )}

          {(subscription.current_period_start || subscription.current_period_end) && (
            <div className="billing-period">
              <div>{t('billing.currentPeriodLabel')}</div>
              <div>{formatDate(subscription.current_period_start)} - {formatDate(subscription.current_period_end)}</div>
            </div>
          )}

          <div className="action-buttons">
            <button className="btn-primary" onClick={handleOpenPortal}>
              {t('billing.manageBillingButton')}
            </button>
            {!subscription.cancel_at_period_end && (
              <button className="btn-secondary" onClick={handleCancelSubscription}>
                {t('billing.cancelSubscriptionButton')}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Usage Overview Card */}
      {usage && (
        <div className="billing-card">
          <div className="card-header">
            <h2>{t('billing.currentUsageHeader')}</h2>
            <button className="btn-link" onClick={fetchBillingData}>
              {t('billing.refreshButton')}
            </button>
          </div>
          <div className="card-body">
            {/* Users */}
            <div className="usage-item">
              <div className="usage-header">
                <span className="usage-label">{t('billing.usersLabel')}</span>
                <span className="usage-count">
                  {usage.active_users}
                  {!usage.is_unlimited_users && `${t('billing.usageRatioSeparator')}${usage.included_users}`}
                  {usage.is_unlimited_users && ` ${t('billing.unlimitedIndicator')}`}
                </span>
              </div>
              {!usage.is_unlimited_users && (
                <div className="usage-bar">
                  <div
                    className="usage-fill"
                    style={{
                      width: `${getUsagePercentage(usage.active_users, usage.included_users, usage.is_unlimited_users)}%`,
                      backgroundColor: usage.users_at_limit ? '#dc3545' : '#ff6800'
                    }}
                  />
                </div>
              )}
              {usage.extra_users_billed > 0 && (
                <div className="extra-charge">
                  {t('billing.extraUsersChargeText', { amount: usage.extra_user_charge.toFixed(2), count: usage.extra_users_billed })}
                </div>
              )}
            </div>

            {/* Integrations — no plan limit; count shown for reference only */}
            <div className="usage-item">
              <div className="usage-header">
                <span className="usage-label">{t('billing.integrationsLabel')}</span>
                <span className="usage-count">
                  {usage.active_integrations} {t('billing.unlimitedIndicator')}
                </span>
              </div>
            </div>

            {/* Posts This Month (monthly creation limit) */}
            <div className="usage-item">
              <div className="usage-header">
                <span className="usage-label">{t('billing.postsThisMonthLabel')}</span>
                <span className="usage-count">
                  {usage.posts_this_month}
                  {!usage.is_unlimited_posts && `${t('billing.usageRatioSeparator')}${usage.included_posts_monthly_created}`}
                  {usage.is_unlimited_posts && ` ${t('billing.unlimitedIndicator')}`}
                </span>
              </div>
              {!usage.is_unlimited_posts && (
                <div className="usage-bar">
                  <div
                    className="usage-fill"
                    style={{
                      width: `${getUsagePercentage(usage.posts_this_month, usage.included_posts_monthly_created, usage.is_unlimited_posts)}%`,
                      backgroundColor: usage.posts_monthly_created_at_limit ? '#dc3545' : '#ff6800'
                    }}
                  />
                </div>
              )}
            </div>

            {/* Scheduled Posts (monthly limit) */}
            <div className="usage-item">
              <div className="usage-header">
                <span className="usage-label">{t('billing.scheduledPostsLabel')}</span>
                <span className="usage-count">
                  {usage.scheduled_posts_count}
                  {!usage.is_unlimited_posts && `${t('billing.usageRatioSeparator')}${usage.included_posts_monthly}`}
                  {usage.is_unlimited_posts && ` ${t('billing.unlimitedIndicator')}`}
                </span>
              </div>
              {!usage.is_unlimited_posts && (
                <div className="usage-bar">
                  <div
                    className="usage-fill"
                    style={{
                      width: `${getUsagePercentage(usage.scheduled_posts_count, usage.included_posts_monthly, usage.is_unlimited_posts)}%`,
                      backgroundColor: usage.posts_monthly_at_limit ? '#dc3545' : '#ff6800'
                    }}
                  />
                </div>
              )}
            </div>

            {/* Event tickets sold this month (bundled allotment) */}
            <div className="usage-item">
              <div className="usage-header">
                <span className="usage-label">{t('billing.ticketsSoldLabel')}</span>
                <span className="usage-count">
                  {usage.tickets_sold_this_period}
                  {!usage.is_unlimited_tickets && `${t('billing.usageRatioSeparator')}${usage.included_tickets_monthly}`}
                  {usage.is_unlimited_tickets && ` ${t('billing.unlimitedIndicator')}`}
                </span>
              </div>
              {!usage.is_unlimited_tickets && (
                <div className="usage-bar">
                  <div
                    className="usage-fill"
                    style={{
                      width: `${getUsagePercentage(usage.tickets_sold_this_period, usage.included_tickets_monthly, usage.is_unlimited_tickets)}%`,
                      backgroundColor: usage.tickets_at_limit ? '#dc3545' : '#ff6800'
                    }}
                  />
                </div>
              )}
              {usage.ticket_overage_count > 0 && (
                <div className="extra-charge">
                  {t('billing.ticketOverageChargeText', {
                    amount: usage.ticket_overage_charge.toFixed(2),
                    count: usage.ticket_overage_count,
                    rate: usage.ticket_overage_rate.toFixed(2),
                  })}
                </div>
              )}
            </div>

            {/* Paid raffle entries sold this month (separate bundled allotment) */}
            <div className="usage-item">
              <div className="usage-header">
                <span className="usage-label">{t('billing.raffleEntriesSoldLabel')}</span>
                <span className="usage-count">
                  {usage.raffle_entries_sold_this_period}
                  {!usage.is_unlimited_raffle_entries && `${t('billing.usageRatioSeparator')}${usage.included_raffle_entries_monthly}`}
                  {usage.is_unlimited_raffle_entries && ` ${t('billing.unlimitedIndicator')}`}
                </span>
              </div>
              {!usage.is_unlimited_raffle_entries && (
                <div className="usage-bar">
                  <div
                    className="usage-fill"
                    style={{
                      width: `${getUsagePercentage(usage.raffle_entries_sold_this_period, usage.included_raffle_entries_monthly, usage.is_unlimited_raffle_entries)}%`,
                      backgroundColor: usage.raffle_entries_at_limit ? '#dc3545' : '#ff6800'
                    }}
                  />
                </div>
              )}
              {usage.raffle_overage_count > 0 && (
                <div className="extra-charge">
                  {t('billing.raffleOverageChargeText', {
                    amount: usage.raffle_overage_charge.toFixed(2),
                    count: usage.raffle_overage_count,
                    rate: usage.raffle_overage_rate.toFixed(2),
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Cost Estimate Card */}
      {cost && (
        <div className="billing-card">
          <div className="card-header">
            <h2>{t('billing.estimatedCostHeader')}</h2>
          </div>
          <div className="card-body">
            <div className="cost-breakdown">
              <div className="cost-item">
                <span>{t('billing.basePlanLabel', { planName: cost.planName })}</span>
                <span>${cost.baseCost.toFixed(2)}</span>
              </div>
              {cost.extraUsers > 0 && (
                <div className="cost-item">
                  <span>{t('billing.extraUsersLabel', { count: cost.extraUsers })}</span>
                  <span>${cost.extraUserCost.toFixed(2)}</span>
                </div>
              )}
              {cost.ticketOverageCount > 0 && (
                <div className="cost-item">
                  <span>{t('billing.ticketOverageLabel', { count: cost.ticketOverageCount })}</span>
                  <span>${cost.ticketOverageCost.toFixed(2)}</span>
                </div>
              )}
              {cost.raffleOverageCount > 0 && (
                <div className="cost-item">
                  <span>{t('billing.raffleOverageLabel', { count: cost.raffleOverageCount })}</span>
                  <span>${cost.raffleOverageCost.toFixed(2)}</span>
                </div>
              )}
              <div className="cost-total">
                <span>{t('billing.totalLabel')}</span>
                <span>${cost.totalCost.toFixed(2)}{t('billing.currencySeparator')}{cost.currency.toUpperCase()}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* App Footer */}
      <AppFooter
        buttons={[
          {
            icon: <FaReceipt />,
            label: "Subscription",
            onClick: () => navigate(`/${actualTenantId}/billing/subscription`),
          },
          {
            icon: <FaHome />,
            label: "Home",
            onClick: () => navigate(`/${actualTenantId}/dashboard`),
          },
          {
            icon: <FaCreditCard />,
            label: "Payment",
            onClick: () => navigate(`/${actualTenantId}/billing/payment-methods`),
          },
        ]}
      />
    </div>
  );
};

export default BillingDashboard;
